/**
 * SceneLoadGate - keeps the tutorial video from resuming before the scene of
 * the chapter it is about to play has been asked for.
 *
 * MUS-576: a chapter jump starts two things at once. It seeks the video, which
 * resumes when the seek completes, and it loads the chapter's scene, on the
 * next animation frame. Nothing ordered the two. On a slow frame the seek can
 * complete first; the video then runs into the chapter's first UpdateObjective
 * before a scene exists to receive it, and the exercise never loads.
 *
 * The gate makes it an order: a seek that completes while a load is still
 * owed does not resume the video; the load, once issued, does.
 *
 * Deliberately free of React and Phaser so it can be unit-tested.
 */
export class SceneLoadGate {
  private loadsOwed = 0;
  private seekWaiting = false;

  /**
   * A jump has asked for a scene and its loader call is still to come. A seek
   * that completed for an earlier jump no longer counts: this jump seeks again.
   */
  loadRequested(): void {
    this.loadsOwed += 1;
    this.seekWaiting = false;
  }

  /**
   * The jump's seek has completed. Returns true when the video may resume now;
   * false when a load is still owed, in which case loadIssued() says when.
   */
  seekCompleted(): boolean {
    if (this.loadsOwed === 0) return true;
    this.seekWaiting = true;
    return false;
  }

  /**
   * A requested loader call has been made. Returns true when a completed seek
   * was waiting for it and no other load is owed: resume the video now.
   */
  loadIssued(): boolean {
    this.loadsOwed = Math.max(0, this.loadsOwed - 1);
    if (this.loadsOwed > 0 || !this.seekWaiting) return false;
    this.seekWaiting = false;
    return true;
  }
}
